type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';
const EVENT = 'themechange';

function isTheme(value: unknown): value is Theme {
	return value === 'light' || value === 'dark';
}

/** Read the current theme from the <html> data-theme attribute. */
export function getTheme(): Theme {
	if (typeof document === 'undefined') return 'light';
	return isTheme(document.documentElement.dataset.theme)
		? document.documentElement.dataset.theme
		: 'light';
}

/** Apply a theme: set the attribute, persist it, and notify subscribers. */
function setTheme(theme: Theme): void {
	if (typeof document === 'undefined') return;
	document.documentElement.dataset.theme = theme;
	try {
		localStorage.setItem(STORAGE_KEY, theme);
	} catch {
		// Storage may be blocked (private mode); the attribute still applies
		// for this session so the theme switch is not lost.
	}
	window.dispatchEvent(new CustomEvent<Theme>(EVENT, { detail: theme }));
}

/** Flip light↔dark and return the new theme. */
export function toggleTheme(): Theme {
	const next: Theme = getTheme() === 'dark' ? 'light' : 'dark';
	setTheme(next);
	return next;
}

/** Subscribe to theme changes. Returns an unsubscribe function. */
export function subscribe(callback: (theme: Theme) => void): () => void {
	// SSR-safe, matching getTheme/setTheme: no window means nothing to listen to.
	if (typeof window === 'undefined') return () => undefined;
	const handler = (event: Event) => {
		const detail = (event as CustomEvent<Theme>).detail;
		if (isTheme(detail)) callback(detail);
	};
	window.addEventListener(EVENT, handler);
	return () => window.removeEventListener(EVENT, handler);
}
